import type { AdminReportDetailDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AuditTab } from './_components/AuditTab';
import { DetailsTab } from './_components/DetailsTab';
import { MediaTab } from './_components/MediaTab';
import { ReportAcceptanceBar } from './_components/ReportAcceptanceBar';
import { ReportDetailHeader } from './_components/ReportDetailHeader';
import { useAdminReportDetail } from '../../api/admin-reports';

/**
 * Report detail drawer — READ-ONLY (AUT-265).
 *
 * Was `ReportModerationDrawer`: a right-side 720px drawer with three
 * tabs and a footer carrying Approve / Request-revision / Reject. All
 * three endpoints were deleted with report moderation, so the footer,
 * its dialogs, its mutation hooks and the FSM gates that decided which
 * buttons to show are gone.
 *
 * The surface has a different job now. The reviewer is the customer, in
 * real time; admin reads a report while adjudicating the APPEAL about
 * it. So this drawer is a reference: media, details, audit trail, plus
 * the acceptance read-out (`ReportAcceptanceBar`) that says whether the
 * customer accepted, when, and whether the 24h timer accepted for them.
 *
 * It holds no mutation of any kind, which is why it is no longer an
 * `observer` — nothing here reads MobX state (permissions are gated at
 * the page level by `ReportsListPage`).
 *
 * Opens when `reportId` is non-null; the detail query is `enabled` only
 * then, so closing it stops the fetch. `staleTime: 4 * 60_000`
 * (architect 🟠 #4) keeps the 1h-TTL signed media URLs fresh.
 */
const DRAWER_WIDTH = 720;

type TabKey = 'media' | 'details' | 'audit';

interface ReportDetailDrawerProps {
  reportId: string | null;
  onClose: () => void;
}

/** Tab nav + body — extracted to keep `DrawerBody` under the size cap. */
const DrawerTabs = ({
  detail,
  tab,
  onTabChange,
}: {
  detail: AdminReportDetailDto;
  tab: TabKey;
  onTabChange: (next: TabKey) => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <Tabs
        value={tab}
        onChange={(_e, v: TabKey) => onTabChange(v)}
        sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider', minHeight: 44 }}
      >
        <Tab
          value="media"
          // AUT-429 — voice notes live on this tab too, so they count.
          label={`${t('reports.detail.tabs.media')} (${detail.media.length + detail.voiceNotes.length})`}
        />
        <Tab value="details" label={t('reports.detail.tabs.details')} />
        <Tab value="audit" label={t('reports.detail.tabs.audit')} />
      </Tabs>
      <Box sx={{ flex: 1, overflow: 'auto', p: '22px 24px' }}>
        {tab === 'media' ? <MediaTab detail={detail} /> : null}
        {tab === 'details' ? <DetailsTab detail={detail} /> : null}
        {tab === 'audit' ? <AuditTab reportId={detail.id} /> : null}
      </Box>
    </>
  );
};

const DrawerBody = ({
  detail,
  onClose,
}: {
  detail: AdminReportDetailDto;
  onClose: () => void;
}): ReactElement => {
  const [tab, setTab] = useState<TabKey>('media');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: { xs: '100vw', sm: DRAWER_WIDTH },
        maxWidth: '100vw',
      }}
    >
      <ReportDetailHeader detail={detail} onClose={onClose} />
      <DrawerTabs detail={detail} tab={tab} onTabChange={setTab} />
      <ReportAcceptanceBar detail={detail} />
    </Box>
  );
};

export const ReportDetailDrawer = ({
  reportId,
  onClose,
}: ReportDetailDrawerProps): ReactElement => {
  const { t } = useTranslation();
  const query = useAdminReportDetail(reportId);

  return (
    <Drawer anchor="right" open={reportId !== null} onClose={onClose}>
      {query.isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      {query.error ? (
        <Box sx={{ width: { xs: '100vw', sm: DRAWER_WIDTH }, p: 3 }}>
          <Alert severity="error">{t('reports.detail.loadError')}</Alert>
        </Box>
      ) : null}
      {query.data ? <DrawerBody key={query.data.id} detail={query.data} onClose={onClose} /> : null}
    </Drawer>
  );
};
